

































































import { Component, Vue } from 'vue-property-decorator';
import BaseModal from '@/components/atoms/BaseModal.vue';
import CourseType from '@/components/atoms/CourseType.vue';
import CourseStatus from '@/components/atoms/CourseStatus.vue';
import SkillsCompleted from '@/components/molecules/SkillsCompleted.vue';
import CourseCardSkeleton from '@/components/atoms/CourseCardSkeleton.vue';
import CourseCardELearning from '@/components/organisms/CourseCardELearning.vue';
import CourseCardWebinar from '@/components/organisms/CourseCardWebinar.vue';
import CourseCardClassroom from '@/components/organisms/CourseCardClassroom.vue';
import NewcrossButton from '@/components/atoms/NewcrossButton.vue';
import HTMLContainer from '@/components/atoms/HTMLContainer.vue';
import ModalButton from '@/components/atoms/ModalButton.vue';
import {
  DoceboCatalogueViewLearningPlan,
  DoceboEnrolmentItemLearningPlan,
  DoceboLearningPlanDetail,
  DoceboLearningPlanDetailCourse,
  DoceboCourseType,
} from '@/lib/DoceboAPI';
import { HealthForceGoSkill } from '@/lib/HealthForceGoAPI';
import calculateExpiryStatus, {
  ExpiryStatus,
} from '@/lib/calculateExpiryStatus';

@Component({
  components: {
    BaseModal,
    CourseType,
    CourseStatus,
    SkillsCompleted,
    CourseCardSkeleton,
    CourseCardELearning,
    CourseCardWebinar,
    CourseCardClassroom,
    NewcrossButton,
    ModalButton,
    HTMLContainer,
  },
})
export default class LearningPlan extends Vue {
  private DoceboCourseType = DoceboCourseType;

  // This enables us to force "close" the cards via the key when the data changes
  private lastUpdatedTimestampKey = new Date().getTime();

  private get buildingId(): string {
    return this.$route.params.buildingId;
  }

  private get learningPlanId(): string {
    return this.$route.params.learningPlanId;
  }

  private get catalogueId(): number {
    return Number(this.$route.params.catalogueId);
  }

  private get learningPlan(): DoceboCatalogueViewLearningPlan {
    return this.$store.state.catalogues.catalogueViews[
      this.catalogueId
    ].data.items.find(
      (item: DoceboCatalogueViewLearningPlan) =>
        item.item_id === this.learningPlanId
    );
  }

  private get enrolment(): DoceboEnrolmentItemLearningPlan {
    return this.$store.state.enrolments.learningPlanEnrolments[
      this.learningPlanId
    ];
  }

  private get learningPlanSkills(): HealthForceGoSkill[] {
    return this.$store.getters['skills/getSkillsForLearningPlan'](
      this.learningPlan.item_id
    );
  }

  private get showRenewButton(): boolean {
    const expiryStatus = calculateExpiryStatus(this.learningPlanSkills);
    return [ExpiryStatus.EXPIRING_SOON, ExpiryStatus.EXPIRED].includes(
      expiryStatus
    );
  }

  private get completed() {
    if (!this.enrolment) return 0;
    return Math.round(
      this.enrolment.courses_count * (this.enrolment.complete_percent / 100)
    );
  }

  private get total() {
    if (!this.enrolment) return 0;
    return this.enrolment.courses_count;
  }

  private get learningPlanDetail(): DoceboLearningPlanDetail | null {
    return this.$store.state.learningPlan.learningPlanDetail[
      this.learningPlanId
    ];
  }

  private get courses(): DoceboLearningPlanDetailCourse[] {
    if (!this.learningPlanDetail || !this.enrolment) return [];
    return this.learningPlanDetail.data.courses;
  }

  mounted() {
    if (!this.learningPlanDetail) {
      this.getLearningPlanDetail();
    }
  }

  private getLearningPlanDetail() {
    return this.$store
      .dispatch('learningPlan/GET_LEARNING_PLAN_DETAIL', this.learningPlanId, {
        root: true,
      })
      .then(() => {
        this.lastUpdatedTimestampKey = new Date().getTime();
      });
  }

  private enrolOnLearningPlan() {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Enrolling');
    this.$store
      .dispatch('enrolments/ENROL_LEARNING_PLAN', this.learningPlanId, {
        root: true,
      })
      .then(
        () => this.getLearningPlanDetail(),
        (error) => {
          // Show error message returned from Docebo (User already enrolled?)
          const dialog = this.$dialog.show({
            title: error.message[0],
            buttons: [
              {
                label: 'Ok',
                handler: () => dialog.dismiss(),
              },
            ],
          });
        }
      )
      .finally(() => this.$store.dispatch('SET_LOADING_OVERLAY', false));
  }

  private resetLearningPlan() {
    const dialog = this.$dialog.show({
      title:
        'Are you sure you wish to renew this learning plan? All previous learning plan progress will be lost.',
      buttons: [
        {
          label: 'Cancel',
          handler: () => dialog.dismiss(),
        },
        {
          label: 'Renew',
          handler: () => {
            dialog.dismiss();
            this.$store.dispatch('SET_LOADING_OVERLAY', 'Renewing');
            this.$store
              .dispatch('enrolments/RESET_LEARNING_PLAN', this.learningPlanId, {
                root: true,
              })
              .then(() => this.getLearningPlanDetail())
              .then(() => this.$store.dispatch('SET_LOADING_OVERLAY', false));
          },
        },
      ],
    });
  }
}
